import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/components/RouteCardPopup.css";
import "../styles/components/TransactionTable.css";
import { useOutsideClick } from "../utils/useOutsideClick";
import { fetchSettings } from "../apis/settings";
import useAutoRefresh from "../hooks/useAutoRefresh";
import {
  createProcessCard,
  createRouteCard,
  fetchRouteCard,
  updateRouteCard,
  startRouteCard,
  finishRouteCard,
  startRouteCardProcess,
  finishRouteCardProcess,
} from "../apis/processCard";
import { displayToast } from "../utils/Toast";
import { ClipboardList, Edit2, Printer } from "lucide-react";
import Loader from "../components/Loader";

const createEmptyRouteRow = () => ({
  operations: "",
  workStation: "",
  timeRequired: "",
  processOrder: "",
  allocated_work_station: "",
  time_taken: "",
  start_time: "",
});

const minimumRouteCardLoaderTime = 350;

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));

const toDisplayValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const formatRouteRow = (item) => ({
  ...item,
  operations: item.operation || "",
  workStation: item.work_station || "",
  timeRequired: toDisplayValue(item.time_required),
  processOrder: toDisplayValue(item.process_order),
  allocated_work_station: item.allocated_work_station || "",
  time_taken: toDisplayValue(item.time_taken),
  start_time:
    item.start_time ||
    item.startTime ||
    item["start time"] ||
    item.process_start_time ||
    "",
});

const getRouteCardItems = (response) =>
  Array.isArray(response?.item_data)
    ? response.item_data
    : Array.isArray(response?.data?.item_data)
      ? response.data.item_data
      : [];

const getRouteCardHead = (response) =>
  response?.head_data || response?.data?.head_data;

const getRouteCardIdFromItems = (items = []) =>
  items.find((item) => item?.route_card_id)?.route_card_id || null;

const getRouteProcessFieldNames = (fields = []) =>
  fields.filter((field) => field.field_name).map((field) => field.field_name);

const defaultProcessFieldNames = ["time_taken", "allocated_work_station"];

const processFieldTitleMap = {
  time_taken: "Time taken",
  allocated_work_station: "Allocated Work Station",
};

const getProcessFieldTitle = (fieldName, routeProcessFields = []) =>
  routeProcessFields.find((field) => field.field_name === fieldName)?.title ||
  processFieldTitleMap[fieldName] ||
  fieldName;

const mergeProcessFieldNames = (fields = []) => {
  const resolved = [
    ...defaultProcessFieldNames,
    ...getRouteProcessFieldNames(fields),
  ];
  return Array.from(new Set(resolved));
};

const numericFieldTypes = new Set([
  "number",
  "numeric",
  "integer",
  "int",
  "decimal",
  "float",
  "double",
]);

const isNumericField = (field = {}) =>
  numericFieldTypes.has(String(field.field_type || "").toLowerCase()) ||
  numericFieldTypes.has(String(field.type || "").toLowerCase()) ||
  numericFieldTypes.has(String(field.data_type || "").toLowerCase());

const isApiSuccess = (response) =>
  response &&
  response.status !== "error" &&
  !response.error &&
  !response.description &&
  !response.message;

const isProcessFinished = (row, processStatus) =>
  processStatus === "finished" ||
  row?.finish === "finished" ||
  row?.status === "finished" ||
  row?.route_card_finish === "finished";

const getStatusValue = (value) => String(value || "").toLowerCase();

const isStartedStatus = (value) =>
  ["started", "start", "in_progress"].includes(getStatusValue(value));

const isFinishedStatus = (value) =>
  ["finished", "finish", "completed", "complete"].includes(
    getStatusValue(value),
  );

const getProcessStatus = (row) => {
  if (
    isFinishedStatus(row?.finish) ||
    isFinishedStatus(row?.status) ||
    isFinishedStatus(row?.route_card_finish) ||
    isFinishedStatus(row?.process_finish)
  ) {
    return "finished";
  }

  if (
    isStartedStatus(row?.start) ||
    isStartedStatus(row?.status) ||
    isStartedStatus(row?.route_card_start) ||
    isStartedStatus(row?.process_start)
  ) {
    return "started";
  }

  return "not_started";
};

const getProcessStatuses = (items = []) =>
  items.reduce((statuses, row, index) => {
    const status = getProcessStatus(row);
    if (status !== "not_started") {
      statuses[index] = status;
    }
    return statuses;
  }, {});

const getRouteCardStatus = (head, items = []) => {
  if (
    isFinishedStatus(head?.finish) ||
    isFinishedStatus(head?.status) ||
    isFinishedStatus(head?.route_card_finish)
  ) {
    return "finished";
  }

  if (
    isStartedStatus(head?.start) ||
    isStartedStatus(head?.status) ||
    isStartedStatus(head?.route_card_start) ||
    items.some((row) => getProcessStatus(row) !== "not_started")
  ) {
    return "started";
  }

  return "not_started";
};

const getCurrentProcessIndex = (items = [], statuses = {}) => {
  const startedProcessIndex = items.findIndex(
    (_, index) => statuses[index] === "started",
  );
  if (startedProcessIndex !== -1) return startedProcessIndex;

  const nextProcessIndex = items.findIndex(
    (_, index) => statuses[index] !== "finished",
  );

  return nextProcessIndex === -1
    ? Math.max(items.length - 1, 0)
    : nextProcessIndex;
};

const RouteCardPopup = ({
  routeCardNo = "",
  productName = "",
  productDescription = "",
  popupCardLabel = "Route Card",
  onClose,
  route_card_created = false,
  onRouteCardSaved,
  product_id,
  jc_tran_id,
  jc_tran_serial_no,
  wo_no,
  route_card_id,
  routeProcessFields = [],
  requireWorkOrderStart = false,
  workOrderStatus = "not_started",
}) => {
  const resolvedCardLabel =
    String(popupCardLabel || "Route Card").trim() || "Route Card";
  const navigate = useNavigate();
  const { tab, category } = useParams();
  const [routeCardId, setRouteCardId] = useState(route_card_id || null);
  const [rows, setRows] = useState([createEmptyRouteRow()]);
  const [backendFields, setBackendFields] = useState(() =>
    mergeProcessFieldNames(routeProcessFields),
  );
  const [operationsOptions, setOperationsOptions] = useState([]);
  const [workStationsOptions, setWorkStationsOptions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState({ row: null, field: null });
  const dropdownRefs = useRef({});
  const dropdownMenuRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(180);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [isRouteCardLoading, setIsRouteCardLoading] = useState(
    Boolean(route_card_id),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasPersistedRouteCard, setHasPersistedRouteCard] = useState(
    route_card_created || Boolean(route_card_id),
  );
  const [headData, setHeadData] = useState({
    product_id: routeCardNo,
    product_name: productName,
    product_desc: productDescription,
    tran_no: routeCardNo ? routeCardNo : "",
  });
  const [localToast, setLocalToast] = useState({ message: "", type: "" });
  const [editableWoNo, setEditableWoNo] = useState(wo_no || "");
  const [routeCardStatus, setRouteCardStatus] = useState("not_started");
  const [processStatuses, setProcessStatuses] = useState({});
  const [currentProcessIndex, setCurrentProcessIndex] = useState(0);
  const onRouteCardSavedRef = useRef(onRouteCardSaved);
  const isSavedRouteCard =
    hasPersistedRouteCard || route_card_created || Boolean(routeCardId);
  const shouldFetchExistingRouteCard = Boolean(jc_tran_id);

  useEffect(() => {
    onRouteCardSavedRef.current = onRouteCardSaved;
  }, [onRouteCardSaved]);

  const routeCardLookupCandidates = useMemo(
    () =>
      jc_tran_id !== null && jc_tran_id !== undefined && jc_tran_id !== ""
        ? [jc_tran_id]
        : [],
    [jc_tran_id],
  );

  const hasRouteCardNumber = Boolean(String(headData.tran_no || "").trim());
  const normalizedWorkOrderStatus = String(workOrderStatus || "")
    .trim()
    .toLowerCase();
  const isParentWorkOrderStarted = [
    "started",
    "start",
    "finished",
    "finish",
    "completed",
    "complete",
  ].includes(normalizedWorkOrderStatus);
  const isWorkOrderStartBlocked =
    requireWorkOrderStart && !isParentWorkOrderStarted;
  const canStartRouteCard =
    isSavedRouteCard && hasRouteCardNumber && !isWorkOrderStartBlocked;

  const allProcessesFinished =
    rows.length > 0 &&
    rows.every((row, index) => isProcessFinished(row, processStatuses[index]));

  const getRowProcessStatus = (row, index) =>
    processStatuses[index] || getProcessStatus(row);

  const topStartButtonLabel =
    routeCardStatus === "started" || routeCardStatus === "finished"
      ? "Started"
      : "Start";
  const topFinishButtonLabel =
    routeCardStatus === "finished" ? "Finished" : "Finish";

  const hasStartedRouteWork =
    routeCardStatus !== "not_started" ||
    rows.some(
      (row, index) => getRowProcessStatus(row, index) !== "not_started",
    );

  const canEditSavedRouteCard = isSavedRouteCard && !hasStartedRouteWork;
  const isRouteCardEditable =
    !isSavedRouteCard || (isEditMode && canEditSavedRouteCard);

  const canEditAllocatedWorkStation = (row, index) =>
    routeCardStatus === "started" &&
    getRowProcessStatus(row, index) === "not_started" &&
    index === currentProcessIndex;

  const canEditTimeTaken = (row, index) =>
    routeCardStatus === "started" &&
    getRowProcessStatus(row, index) === "started";

  const showLocalToast = (message, type = "error") => {
    setLocalToast({ message, type });
    setTimeout(() => setLocalToast({ message: "", type: "" }), 2500);
  };

  const formatStartTime = (time) => {
    if (!time) return "-";
    const d = new Date(time);
    if (isNaN(d)) return "-";
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  useEffect(() => {
    setBackendFields((currentFields) => {
      const fieldNames = mergeProcessFieldNames(routeProcessFields);
      return Array.from(new Set([...currentFields, ...fieldNames]));
    });
  }, [routeProcessFields]);

  useEffect(() => {
    if (route_card_id) {
      setRouteCardId(route_card_id);
    }
  }, [route_card_id]);

  useEffect(() => {
    if (hasStartedRouteWork && isEditMode) {
      setIsEditMode(false);
    }
  }, [hasStartedRouteWork, isEditMode]);

  const fetchRouteCardData = useCallback(
    async (rowsToPreserve = [], silent = false) => {
      if (
        !shouldFetchExistingRouteCard ||
        routeCardLookupCandidates.length === 0
      ) {
        setIsRouteCardLoading(false);
        return null;
      }

      if (!silent) setIsRouteCardLoading(true);
      const loadingStartedAt = Date.now();
      try {
        let response = null;

        for (const lookupId of routeCardLookupCandidates) {
          try {
            const candidateResponse = await fetchRouteCard(lookupId);
            if (
              getRouteCardHead(candidateResponse) ||
              getRouteCardItems(candidateResponse).length > 0
            ) {
              response = candidateResponse;
              break;
            }
          } catch (error) {}
        }

        if (!response) {
          return null;
        }

        const headDataResponse = getRouteCardHead(response);
        const itemData = getRouteCardItems(response);
        const formattedRows = itemData.map(formatRouteRow);
        const existingRouteCardId =
          headDataResponse?.id ||
          headDataResponse?.route_card_id ||
          headDataResponse?.tran_id ||
          getRouteCardIdFromItems(itemData) ||
          route_card_id ||
          null;

        if (headDataResponse) {
          setRouteCardId(existingRouteCardId);

          setHeadData({
            product_id: headDataResponse.product_id,
            product_name: headDataResponse.product_name,
            product_desc: headDataResponse.product_desc || "",
            tran_no: headDataResponse.route_card_no,
          });

          setEditableWoNo(
            headDataResponse.wo_no || editableWoNo || wo_no || "",
          );
        }

        if (formattedRows.length > 0) {
          const nextProcessStatuses = getProcessStatuses(formattedRows);
          setRows((currentRows) =>
            formattedRows.map((row, index) => {
              const preservedRows = rowsToPreserve.length
                ? rowsToPreserve
                : currentRows;
              const fallbackRow =
                preservedRows.find(
                  (preservedRow) =>
                    preservedRow.serial_no &&
                    preservedRow.serial_no === row.serial_no,
                ) ||
                preservedRows[index] ||
                {};

              return {
                ...row,
                allocated_work_station:
                  row.allocated_work_station ||
                  fallbackRow.allocated_work_station ||
                  "",
                time_taken: row.time_taken || fallbackRow.time_taken || "",
                start_time: row.start_time || fallbackRow.start_time || "",
              };
            }),
          );
          setProcessStatuses(nextProcessStatuses);
          setRouteCardStatus(
            getRouteCardStatus(headDataResponse, formattedRows),
          );
          setCurrentProcessIndex(
            getCurrentProcessIndex(formattedRows, nextProcessStatuses),
          );

          if (!headDataResponse) {
            const itemRouteCardId = getRouteCardIdFromItems(itemData);
            if (itemRouteCardId) {
              setRouteCardId(itemRouteCardId);
            }
          }
        } else if (headDataResponse) {
          setRouteCardStatus(getRouteCardStatus(headDataResponse));
        }

        if (headDataResponse || itemData.length > 0) {
          setHasPersistedRouteCard(true);
          setIsEditMode(false);
        }

        if (onRouteCardSavedRef.current) {
          onRouteCardSavedRef.current(existingRouteCardId, response);
        }

        return response;
      } catch (err) {
        return null;
      } finally {
        if (!silent) {
          const elapsedTime = Date.now() - loadingStartedAt;
          const remainingTime = minimumRouteCardLoaderTime - elapsedTime;
          if (remainingTime > 0) {
            await wait(remainingTime);
          }
          setIsRouteCardLoading(false);
        }
      }
    },
    [shouldFetchExistingRouteCard, routeCardLookupCandidates, route_card_id],
  );

  useAutoRefresh(() => fetchRouteCardData([], true), {
    enabled:
      isSavedRouteCard &&
      !isEditMode &&
      shouldFetchExistingRouteCard &&
      routeCardLookupCandidates.length > 0,
  });

  const handleRouteCardStart = async () => {
    if (isWorkOrderStartBlocked) {
      showLocalToast(
        "Start Work Order first before starting Route Card",
        "error",
      );
      return;
    }

    if (!routeCardId) {
      showLocalToast("Route Card ID not found", "error");
      return;
    }

    try {
      const response = await startRouteCard(routeCardId);
      if (isApiSuccess(response)) {
        setRouteCardStatus("started");
        showLocalToast("Route Card started successfully", "success");
        setCurrentProcessIndex(0);
        await fetchRouteCardData();
      } else {
        showLocalToast(
          response?.description ||
            response?.message ||
            "Failed to start Route Card",
          "error",
        );
      }
    } catch (error) {
      showLocalToast("Error starting Route Card", "error");
    }
  };

  const handleRouteCardFinish = async () => {
    if (isWorkOrderStartBlocked) {
      showLocalToast(
        "Start Work Order first before finishing Route Card",
        "error",
      );
      return;
    }

    if (!routeCardId) {
      showLocalToast("Route Card ID not found", "error");
      return;
    }

    if (!allProcessesFinished) {
      showLocalToast(
        "All processes must be finished before completing Route Card",
        "error",
      );
      return;
    }

    try {
      const response = await finishRouteCard(routeCardId);
      if (isApiSuccess(response)) {
        setRouteCardStatus("finished");
        showLocalToast("Route Card finished successfully", "success");

        if (onRouteCardSavedRef.current) {
          const responseHead =
            response?.head_data || response?.data?.head_data || {};
          const normalizedRows = rows.map((row, index) => ({
            ...row,
            serial_no: row.serial_no || index + 1,
          }));

          onRouteCardSavedRef.current(routeCardId, {
            ...response,
            head_data: {
              ...responseHead,
              route_card_id: responseHead?.route_card_id || routeCardId,
              route_card_finish: "finished",
              finish: "finished",
              status: "finished",
              route_card_no:
                responseHead?.route_card_no || headData?.tran_no || "",
            },
            item_data: normalizedRows,
          });
        }

        await fetchRouteCardData();
      } else {
        showLocalToast(
          response?.description ||
            response?.message ||
            "Failed to finish Route Card",
          "error",
        );
      }
    } catch (error) {
      showLocalToast("Error finishing Route Card", "error");
    }
  };

  const handleProcessStart = async (processIndex) => {
    if (isWorkOrderStartBlocked) {
      showLocalToast("Start Work Order first before starting process", "error");
      return;
    }

    if (!routeCardId) {
      showLocalToast("Route Card ID not found", "error");
      return;
    }

    const row = rows[processIndex];
    const allocatedWorkStation = row.allocated_work_station;

    if (!allocatedWorkStation) {
      showLocalToast(
        "Allocated Work Station is required to start process",
        "error",
      );
      return;
    }

    try {
      const startedAt = new Date();
      const response = await startRouteCardProcess(
        routeCardId,
        row.serial_no || processIndex + 1,
        allocatedWorkStation,
        startedAt.toISOString(),
      );

      if (isApiSuccess(response)) {
        const rowsWithAllocatedWorkStation = rows.map((currentRow, index) =>
          index === processIndex
            ? {
                ...currentRow,
                allocated_work_station: allocatedWorkStation,
                start_time: currentRow.start_time || startedAt.toISOString(),
              }
            : currentRow,
        );

        setRows(rowsWithAllocatedWorkStation);
        setProcessStatuses((prev) => ({
          ...prev,
          [processIndex]: "started",
        }));
        await fetchRouteCardData(rowsWithAllocatedWorkStation);
        setRouteCardStatus("started");
        setCurrentProcessIndex(processIndex);
        setProcessStatuses((prev) => ({
          ...prev,
          [processIndex]: "started",
        }));
        showLocalToast(
          `Process ${processIndex + 1} started successfully`,
          "success",
        );
      } else {
        showLocalToast(
          response?.description ||
            response?.message ||
            "Failed to start process",
          "error",
        );
      }
    } catch (error) {
      showLocalToast("Error starting process", "error");
    }
  };

  const handleProcessFinish = async (processIndex) => {
    if (isWorkOrderStartBlocked) {
      showLocalToast(
        "Start Work Order first before finishing process",
        "error",
      );
      return;
    }

    if (!routeCardId) {
      showLocalToast("Route Card ID not found", "error");
      return;
    }

    try {
      const row = rows[processIndex];
      const startTimeValue = row.start_time;
      const startTime = startTimeValue ? new Date(startTimeValue) : null;

      if (!startTime || isNaN(startTime)) {
        showLocalToast(
          "Start time not available. Please start the process first.",
          "error",
        );
        return;
      }

      const timeTaken = Math.max(
        1,
        Math.round((new Date() - startTime) / 60000),
      );

      const response = await finishRouteCardProcess(
        routeCardId,
        row.serial_no || processIndex + 1,
        timeTaken,
        startTimeValue || null,
      );
      if (isApiSuccess(response)) {
        const updatedRows = rows.map((r, i) =>
          i === processIndex ? { ...r, time_taken: String(timeTaken) } : r,
        );
        setRows(updatedRows);
        setProcessStatuses((prev) => ({
          ...prev,
          [processIndex]: "finished",
        }));
        await fetchRouteCardData(updatedRows);
        setRouteCardStatus("started");
        setProcessStatuses((prev) => ({
          ...prev,
          [processIndex]: "finished",
        }));
        showLocalToast(
          `Process ${processIndex + 1} finished successfully`,
          "success",
        );

        if (processIndex + 1 < rows.length) {
          setCurrentProcessIndex(processIndex + 1);
        }
      } else {
        showLocalToast(
          response?.description ||
            response?.message ||
            "Failed to finish process",
          "error",
        );
      }
    } catch (error) {
      showLocalToast("Error finishing process", "error");
    }
  };

  useEffect(() => {
    if (openDropdown.row === null) return;
    function handleClick(e) {
      const triggerKey = `${openDropdown.field}-${openDropdown.row}`;
      const triggerEl = dropdownRefs.current[triggerKey];
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(e.target) &&
        (!triggerEl || !triggerEl.contains(e.target))
      ) {
        setOpenDropdown({ row: null, field: null });
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [openDropdown]);

  useEffect(() => {
    if (loadingOptions) return;
    const getMaxWidth = (options) => {
      if (!options || options.length === 0) return 180;
      const temp = document.createElement("span");
      temp.style.visibility = "hidden";
      temp.style.position = "absolute";
      temp.style.fontSize = "16px";
      temp.style.fontFamily = "inherit";
      document.body.appendChild(temp);
      let max = 180;
      options.forEach((opt) => {
        temp.textContent = opt.name;
        max = Math.max(max, temp.offsetWidth + 48);
      });
      document.body.removeChild(temp);
      return max;
    };
    const opsW = getMaxWidth(operationsOptions);
    const wsW = getMaxWidth(workStationsOptions);
    setDropdownWidth(Math.max(opsW, wsW));
  }, [operationsOptions, workStationsOptions, loadingOptions]);

  useEffect(() => {
    async function fetchDropdowns() {
      try {
        setLoadingOptions(true);
        const operationsRes = await fetchSettings("operations");
        const workStationsRes = await fetchSettings("work_stations");
        setOperationsOptions(operationsRes.items || []);
        setWorkStationsOptions(workStationsRes.items || []);
      } catch (err) {
        setOperationsOptions([]);
        setWorkStationsOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (shouldFetchExistingRouteCard) {
      fetchRouteCardData();
      return;
    }
    setIsRouteCardLoading(false);
  }, [fetchRouteCardData, shouldFetchExistingRouteCard]);

  const handleAddRow = () => {
    setRows([...rows, createEmptyRouteRow()]);
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const handleRemoveRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const getBackendItemFields = (row) =>
    backendFields.reduce((itemFields, fieldName) => {
      const value = row[fieldName];
      if (value === "" || value === null || value === undefined) {
        return itemFields;
      }

      if (fieldName === "time_taken") {
        const numericValue = toOptionalInteger(value);
        if (numericValue !== null) {
          itemFields[fieldName] = numericValue;
        }
        return itemFields;
      }

      const field = routeProcessFields.find(
        (processField) => processField.field_name === fieldName,
      );

      if (isNumericField(field)) {
        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
          itemFields[fieldName] = numericValue;
        }
        return itemFields;
      }

      itemFields[fieldName] = value;
      return itemFields;
    }, {});

  const toOptionalInteger = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.trunc(numericValue) : null;
  };

  const buildProcessCardPayload = () => ({
    head_data: {
      product_id: product_id,
      product_name: productName || "",
      product_desc: productDescription || productName || "",
      tran_no: headData.tran_no,
    },
    item_data: rows.map((row, index) => ({
      serial_no: index + 1,
      operation: row.operations,
      work_station: row.workStation,
      time_required: parseInt(row.timeRequired, 10),
      process_order: parseInt(row.processOrder, 10),
    })),
  });

  const buildRouteCardPayload = () => {
    const routeCardPayloadId = routeCardId || jc_tran_id;

    return {
      head_data: {
        product_id: product_id,
        product_name: productName || "",
        product_desc: productDescription || productName || "",
        jc_tran_id: jc_tran_id,
        tran_no: headData.tran_no,
        route_card_no: headData.tran_no,
        jc_tran_serial_no: jc_tran_serial_no,
        wo_no: editableWoNo,
        ...(routeCardId && { id: routeCardId }),
      },
      item_data: rows.map((row, index) => ({
        serial_no: index + 1,
        operation: row.operations,
        work_station: row.workStation,
        time_required: parseInt(row.timeRequired, 10),
        process_order: parseInt(row.processOrder, 10),
        time_taken: toOptionalInteger(row.time_taken),
        route_card_id: routeCardPayloadId,
        product_id: product_id,
        ...getBackendItemFields(row),
      })),
    };
  };

  const handlePrintClick = () => {
    const printRows = rows.map((row, index) => ({
      ...row,
      serial_no: row.serial_no || index + 1,
      operation: row.operation || row.operations,
      work_station: row.work_station || row.workStation,
      time_required: row.time_required || row.timeRequired,
      process_order: row.process_order || row.processOrder,
      process_status: getRowProcessStatus(row, index),
    }));

    navigate(
      `/${tab || "master"}/${category || "product"}/transactions/invoice`,
      {
        state: {
          formData: {
            headDetails: {
              route_card_no: headData.tran_no || routeCardNo,
              product_id,
              product_name: productName,
              product_desc: productDescription || productName || "",
              jc_tran_id,
              jc_tran_serial_no,
              wo_no: editableWoNo || wo_no,
            },
            itemDetails: printRows,
            rowData: [
              { field_name: "operation", title: "Operations" },
              { field_name: "work_station", title: "Work Station" },
              { field_name: "time_required", title: "Time Required" },
              { field_name: "process_order", title: "Process Order" },
              ...routeProcessFields.filter((field) => field.field_name),
              { field_name: "process_status", title: "Status" },
            ],
            transactionCode: "ROUTE_CARD",
          },
        },
      },
    );
  };

  const handleSave = async () => {
    if (
      !product_id ||
      !jc_tran_id ||
      !jc_tran_serial_no ||
      !editableWoNo ||
      !headData.tran_no
    ) {
      const missingFields = [];
      if (!product_id) missingFields.push("Product ID");
      if (!jc_tran_id) missingFields.push("Job Card ID");
      if (!jc_tran_serial_no) missingFields.push("Job Card Serial No");
      if (!editableWoNo) missingFields.push("Work Order No");
      if (!headData.tran_no) missingFields.push(`${resolvedCardLabel} No`);

      const errorMessage = `Missing required fields: ${missingFields.join(
        ", ",
      )}`;
      displayToast(errorMessage, "error");
      return;
    }

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (
        !row.operations ||
        !row.workStation ||
        !row.timeRequired ||
        !row.processOrder
      ) {
        const errorMessage = `All fields are required in row ${i + 1}`;
        displayToast(errorMessage, "error");
        return;
      }

      if (isNaN(row.timeRequired) || isNaN(row.processOrder)) {
        const errorMessage = `Time Required and Process Order must be numbers in row ${
          i + 1
        }`;
        displayToast(errorMessage, "error");
        return;
      }
    }

    setIsSaving(true);
    try {
      const submissionData = buildRouteCardPayload();

      let response;

      if (isSavedRouteCard && routeCardId) {
        response = await updateRouteCard(routeCardId, submissionData);
      } else {
        const processCardResponse = await createProcessCard(
          buildProcessCardPayload(),
        );
        if (
          !getRouteCardHead(processCardResponse) &&
          !isApiSuccess(processCardResponse)
        ) {
          displayToast(
            processCardResponse?.description ||
              processCardResponse?.message ||
              "Failed to save process card",
            "error",
          );
          return;
        }

        response = await createRouteCard(submissionData);
      }

      if (getRouteCardHead(response)) {
        const headDataResponse = getRouteCardHead(response);
        const itemData = getRouteCardItems(response);
        const savedRouteCardId =
          headDataResponse?.id ||
          headDataResponse?.route_card_id ||
          headDataResponse?.tran_id ||
          getRouteCardIdFromItems(itemData) ||
          routeCardId;

        if (savedRouteCardId) {
          setRouteCardId(savedRouteCardId);
        }

        setHeadData({
          product_id: headDataResponse.product_id,
          product_name: headDataResponse.product_name,
          product_desc: headDataResponse.product_desc || "",
          tran_no: headDataResponse.route_card_no,
        });

        setEditableWoNo(headDataResponse.wo_no || editableWoNo || wo_no || "");

        if (itemData.length > 0) {
          const formattedRows = itemData.map(formatRouteRow);
          const nextProcessStatuses = getProcessStatuses(formattedRows);
          setRows(formattedRows);
          setProcessStatuses(nextProcessStatuses);
          setRouteCardStatus(
            getRouteCardStatus(headDataResponse, formattedRows),
          );
          setCurrentProcessIndex(
            getCurrentProcessIndex(formattedRows, nextProcessStatuses),
          );
        }

        displayToast("Route Card created/updated successfully", "success");
        setHasPersistedRouteCard(true);
        setIsEditMode(false);
        if (onRouteCardSaved) onRouteCardSaved(savedRouteCardId, response);
      } else {
        const errorMessage =
          response?.description || "Failed to save route card";
        displayToast(errorMessage, "error");
      }
    } catch (err) {
      const errorMessage = err?.message || "Failed to save route card";
      displayToast(errorMessage, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const popupRef = useOutsideClick(() => {
    if (onClose) onClose();
  });

  const getDropdownFixedStyle = (triggerEl) => {
    if (!triggerEl) return { left: 0, top: 0, width: 200 };
    const rect = triggerEl.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.bottom + 4,
      width: rect.width,
    };
  };

  return (
    <div className="route-card-popup-overlay">
      <div className="route-card-popup-container" ref={popupRef}>
        {localToast.message && (
          <div className={`process-card-toast ${localToast.type}`}>
            {localToast.message}
          </div>
        )}
        <div className="route-card-popup-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ClipboardList
              size={24}
              color="#ffffff"
              style={{ marginRight: 4 }}
            />
            {resolvedCardLabel}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              className="start-btn"
              onClick={handleRouteCardStart}
              disabled={
                isEditMode ||
                isWorkOrderStartBlocked ||
                routeCardStatus !== "not_started" ||
                !canStartRouteCard
              }
            >
              {topStartButtonLabel}
            </button>
            <button
              className="finish-btn"
              onClick={handleRouteCardFinish}
              disabled={
                isEditMode ||
                isWorkOrderStartBlocked ||
                routeCardStatus !== "started" ||
                !allProcessesFinished
              }
            >
              {topFinishButtonLabel}
            </button>
            {isSavedRouteCard && !isEditMode && (
              <button
                type="button"
                onClick={() => canEditSavedRouteCard && setIsEditMode(true)}
                disabled={!canEditSavedRouteCard}
                title={
                  canEditSavedRouteCard
                    ? `Edit ${resolvedCardLabel}`
                    : `${resolvedCardLabel} cannot be edited after start`
                }
                style={{
                  background: "transparent",
                  border: 0,
                  color: "inherit",
                  cursor: canEditSavedRouteCard ? "pointer" : "not-allowed",
                  opacity: canEditSavedRouteCard ? 1 : 0.45,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Edit2 size={22} />
              </button>
            )}
            <Printer
              style={{ cursor: "pointer" }}
              size={22}
              onClick={handlePrintClick}
              title={`Print ${resolvedCardLabel}`}
            />
            {onClose && (
              <button className="route-card-popup-close" onClick={onClose}>
                &times;
              </button>
            )}
          </div>
        </div>
        {isRouteCardLoading ? (
          <div
            style={{
              minHeight: "360px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Loader />
          </div>
        ) : (
          <>
            <div className="route-card-popup-head-section">
              <div className="route-card-popup-head-field">
                <label>{`${resolvedCardLabel} No.`}</label>
                <input
                  type="text"
                  value={headData.tran_no}
                  className="route-card-popup-input"
                  placeholder={`Enter ${resolvedCardLabel} No.`}
                  disabled={isSavedRouteCard}
                  onChange={(e) =>
                    !isSavedRouteCard &&
                    setHeadData({ ...headData, tran_no: e.target.value })
                  }
                />
              </div>
              <div className="route-card-popup-head-field">
                <label>Product Name</label>
                <div className="route-card-popup-head-value min-line">
                  {productName || "-"}
                </div>
              </div>
              <div className="route-card-popup-head-field">
                <label>Product Description</label>
                <div className="route-card-popup-head-value min-line">
                  {productDescription || "-"}
                </div>
              </div>
              <div className="route-card-popup-head-field">
                <label>{`${resolvedCardLabel} Serial No`}</label>
                <div className="route-card-popup-head-value min-line">
                  {jc_tran_serial_no || "-"}
                </div>
              </div>
              <div className="route-card-popup-head-field">
                <label>Work Order No</label>
                {isSavedRouteCard ? (
                  isEditMode ? (
                    <input
                      type="text"
                      value={editableWoNo}
                      onChange={(e) => setEditableWoNo(e.target.value)}
                      className="route-card-popup-input"
                      placeholder="Enter Work Order No."
                    />
                  ) : (
                    <div className="route-card-popup-head-value min-line">
                      {editableWoNo || "-"}
                    </div>
                  )
                ) : (
                  <div className="route-card-popup-head-value min-line">
                    {wo_no || "-"}
                  </div>
                )}
              </div>
            </div>
            <div className="transaction-card">
              <div
                className="transaction-card-content"
                style={{ position: "relative" }}
              >
                <div className="table-container">
                  <table className="transaction-table">
                    <thead>
                      <tr>
                        <th>Sl.No</th>
                        <th>
                          Operations
                          <span style={{ color: "#222", fontSize: "13px" }}>
                            {" "}
                            *
                          </span>
                        </th>
                        <th>
                          Work Station
                          <span style={{ color: "#222", fontSize: "13px" }}>
                            {" "}
                            *
                          </span>
                        </th>
                        <th>
                          Time Required
                          <span style={{ color: "#222", fontSize: "13px" }}>
                            {" "}
                            *
                          </span>
                        </th>
                        <th>
                          Process Order
                          <span style={{ color: "#222", fontSize: "13px" }}>
                            {" "}
                            *
                          </span>
                        </th>
                        <th>Start Time</th>
                        {backendFields.map((fieldName) => (
                          <th key={fieldName}>
                            {getProcessFieldTitle(
                              fieldName,
                              routeProcessFields,
                            )}
                          </th>
                        ))}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className={`modal-dropdown-container`}
                              style={{
                                position: "relative",
                                width: dropdownWidth,
                              }}
                            >
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current[`operations-${idx}`] =
                                    el)
                                }
                                className={`customer-data-modal-input master-data-modal-select-wrapper${
                                  openDropdown.row === idx &&
                                  openDropdown.field === "operations"
                                    ? " dropdown-open-highlight"
                                    : ""
                                }`}
                                style={{
                                  width: dropdownWidth,
                                  pointerEvents: isRouteCardEditable
                                    ? "auto"
                                    : "none",
                                  background: !isRouteCardEditable
                                    ? "#f9fafb"
                                    : undefined,
                                }}
                                onClick={() => {
                                  if (isRouteCardEditable)
                                    setOpenDropdown(
                                      openDropdown.row === idx &&
                                        openDropdown.field === "operations"
                                        ? { row: null, field: null }
                                        : { row: idx, field: "operations" },
                                    );
                                }}
                                tabIndex={0}
                              >
                                <span className="modal-dropdown-text">
                                  {row.operations || "Select Operation"}
                                </span>
                                {isRouteCardEditable && (
                                  <span className="dropdown-icon">▼</span>
                                )}
                              </div>
                              {openDropdown.row === idx &&
                                openDropdown.field === "operations" &&
                                dropdownRefs.current[`operations-${idx}`] && (
                                  <div
                                    ref={dropdownMenuRef}
                                    className="primary-dropdown-options process-card-popup-dropdown-fixed"
                                    style={getDropdownFixedStyle(
                                      dropdownRefs.current[`operations-${idx}`],
                                    )}
                                  >
                                    {operationsOptions.map((opt) => (
                                      <div
                                        key={opt.id}
                                        className={`primary-dropdown-option${
                                          row.operations === opt.name
                                            ? " selected-option"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          handleInputChange(
                                            idx,
                                            "operations",
                                            opt.name,
                                          );
                                          setOpenDropdown({
                                            row: null,
                                            field: null,
                                          });
                                        }}
                                      >
                                        {opt.name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          </td>
                          <td>
                            <div
                              className={`modal-dropdown-container`}
                              style={{
                                position: "relative",
                                width: dropdownWidth,
                              }}
                            >
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current[`workStation-${idx}`] =
                                    el)
                                }
                                className={`customer-data-modal-input master-data-modal-select-wrapper${
                                  openDropdown.row === idx &&
                                  openDropdown.field === "workStation"
                                    ? " dropdown-open-highlight"
                                    : ""
                                }`}
                                style={{
                                  width: dropdownWidth,
                                  pointerEvents: isRouteCardEditable
                                    ? "auto"
                                    : "none",
                                  background: !isRouteCardEditable
                                    ? "#f9fafb"
                                    : undefined,
                                }}
                                onClick={() => {
                                  if (isRouteCardEditable)
                                    setOpenDropdown(
                                      openDropdown.row === idx &&
                                        openDropdown.field === "workStation"
                                        ? { row: null, field: null }
                                        : { row: idx, field: "workStation" },
                                    );
                                }}
                                tabIndex={0}
                              >
                                <span className="modal-dropdown-text">
                                  {row.workStation || "Select Work Station"}
                                </span>
                                {isRouteCardEditable && (
                                  <span className="dropdown-icon">▼</span>
                                )}
                              </div>
                              {openDropdown.row === idx &&
                                openDropdown.field === "workStation" &&
                                dropdownRefs.current[`workStation-${idx}`] && (
                                  <div
                                    ref={dropdownMenuRef}
                                    className="primary-dropdown-options process-card-popup-dropdown-fixed"
                                    style={getDropdownFixedStyle(
                                      dropdownRefs.current[
                                        `workStation-${idx}`
                                      ],
                                    )}
                                  >
                                    {workStationsOptions.map((opt) => (
                                      <div
                                        key={opt.id}
                                        className={`primary-dropdown-option${
                                          row.workStation === opt.name
                                            ? " selected-option"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          handleInputChange(
                                            idx,
                                            "workStation",
                                            opt.name,
                                          );
                                          setOpenDropdown({
                                            row: null,
                                            field: null,
                                          });
                                        }}
                                      >
                                        {opt.name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          </td>
                          <td>
                            {!isRouteCardEditable ? (
                              <div className="table-cell-value">
                                {row.timeRequired}
                              </div>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                value={row.timeRequired}
                                onChange={(e) =>
                                  handleInputChange(
                                    idx,
                                    "timeRequired",
                                    e.target.value,
                                  )
                                }
                                className="route-card-popup-input"
                              />
                            )}
                          </td>
                          <td>
                            {!isRouteCardEditable ? (
                              <div className="table-cell-value">
                                {row.processOrder}
                              </div>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                value={row.processOrder}
                                onChange={(e) =>
                                  handleInputChange(
                                    idx,
                                    "processOrder",
                                    e.target.value,
                                  )
                                }
                                className="route-card-popup-input"
                              />
                            )}
                          </td>
                          <td>
                            <div className="table-cell-value">
                              {formatStartTime(row.start_time)}
                            </div>
                          </td>
                          {backendFields.map((fieldName) => (
                            <td key={fieldName}>
                              {fieldName === "allocated_work_station" ? (
                                !canEditAllocatedWorkStation(row, idx) ? (
                                  <div className="table-cell-value">
                                    {row.allocated_work_station || "-"}
                                  </div>
                                ) : (
                                  <div
                                    className={`modal-dropdown-container`}
                                    style={{
                                      position: "relative",
                                      width: dropdownWidth,
                                    }}
                                  >
                                    <div
                                      ref={(el) =>
                                        (dropdownRefs.current[
                                          `allocatedWorkStation-${idx}`
                                        ] = el)
                                      }
                                      className={`customer-data-modal-input master-data-modal-select-wrapper${
                                        openDropdown.row === idx &&
                                        openDropdown.field ===
                                          "allocatedWorkStation"
                                          ? " dropdown-open-highlight"
                                          : ""
                                      }`}
                                      style={{ width: dropdownWidth }}
                                      onClick={() =>
                                        setOpenDropdown(
                                          openDropdown.row === idx &&
                                            openDropdown.field ===
                                              "allocatedWorkStation"
                                            ? { row: null, field: null }
                                            : {
                                                row: idx,
                                                field: "allocatedWorkStation",
                                              },
                                        )
                                      }
                                      tabIndex={0}
                                    >
                                      <span className="modal-dropdown-text">
                                        {row.allocated_work_station ||
                                          "Select Work Station"}
                                      </span>
                                      <span className="dropdown-icon">▼</span>
                                    </div>
                                    {openDropdown.row === idx &&
                                      openDropdown.field ===
                                        "allocatedWorkStation" &&
                                      dropdownRefs.current[
                                        `allocatedWorkStation-${idx}`
                                      ] && (
                                        <div
                                          ref={dropdownMenuRef}
                                          className="primary-dropdown-options process-card-popup-dropdown-fixed"
                                          style={getDropdownFixedStyle(
                                            dropdownRefs.current[
                                              `allocatedWorkStation-${idx}`
                                            ],
                                          )}
                                        >
                                          {workStationsOptions.map((opt) => (
                                            <div
                                              key={opt.id}
                                              className={`primary-dropdown-option${
                                                row.allocated_work_station ===
                                                opt.name
                                                  ? " selected-option"
                                                  : ""
                                              }`}
                                              onClick={() => {
                                                handleInputChange(
                                                  idx,
                                                  "allocated_work_station",
                                                  opt.name,
                                                );
                                                setOpenDropdown({
                                                  row: null,
                                                  field: null,
                                                });
                                              }}
                                            >
                                              {opt.name}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                )
                              ) : fieldName === "time_taken" ? (
                                <div className="table-cell-value">
                                  {row.time_taken || "-"}
                                </div>
                              ) : (
                                <div className="table-cell-value">
                                  {row[fieldName]}
                                </div>
                              )}
                            </td>
                          ))}
                          <td>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <button
                                className="start-btn-row"
                                onClick={() => handleProcessStart(idx)}
                                disabled={
                                  isEditMode ||
                                  isWorkOrderStartBlocked ||
                                  routeCardStatus !== "started" ||
                                  getRowProcessStatus(row, idx) === "started" ||
                                  getRowProcessStatus(row, idx) ===
                                    "finished" ||
                                  idx !== currentProcessIndex
                                }
                              >
                                {getRowProcessStatus(row, idx) === "started" ||
                                getRowProcessStatus(row, idx) === "finished"
                                  ? "Started"
                                  : "Start"}
                              </button>
                              <button
                                className="finish-btn-row"
                                onClick={() => handleProcessFinish(idx)}
                                disabled={
                                  isEditMode ||
                                  isWorkOrderStartBlocked ||
                                  routeCardStatus !== "started" ||
                                  getRowProcessStatus(row, idx) !== "started"
                                }
                              >
                                {getRowProcessStatus(row, idx) === "finished"
                                  ? "Finished"
                                  : "Finish"}
                              </button>
                              <button
                                className="remove-row-btn"
                                onClick={() => handleRemoveRow(idx)}
                                disabled={
                                  rows.length === 1 ||
                                  hasStartedRouteWork ||
                                  (isSavedRouteCard && !isEditMode)
                                }
                                title="Remove Row"
                              >
                                &times;
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="add-item-container-process-card">
                  <button
                    className="add-item-btn-process-card"
                    onClick={handleAddRow}
                    disabled={
                      hasStartedRouteWork || (isSavedRouteCard && !isEditMode)
                    }
                  >
                    Add Item
                    <svg
                      className="plus-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>
                <div className="table-save-button-container-process-card">
                  <button
                    className="next-btn-process-card"
                    onClick={handleSave}
                    disabled={
                      isSaving ||
                      hasStartedRouteWork ||
                      (isSavedRouteCard && !isEditMode)
                    }
                  >
                    {isSaving ? "Saving..." : "SAVE"}
                  </button>
                  <p className="route-card-save-note">
                    {`Enter ${resolvedCardLabel} No. and all required fields, then click Save. Start will be enabled only after successful save.`}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RouteCardPopup;
